import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CopyPlus, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { usePlanEditor } from './PlanEditorContext';
import { FoldableSection } from './FoldableSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DuplicateFloorControl — V14.78 « Dupliquer depuis un étage ».
 * Copie murs porteurs + escaliers + menuiseries extérieures + contour d'un
 * autre niveau : coordonnées identiques → superposition verticale parfaite.
 * Il ne reste plus qu'à tracer cloisons + menuiseries intérieures.
 */
export const DuplicateFloorControl = ({ projectId, selectedLotId, disabled }) => {
  const { duplicateFromFloor } = usePlanEditor();
  const [available, setAvailable] = useState([]);
  const [sourceLotId, setSourceLotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({ porteurs: true, withStairs: true, extOpenings: true, withOuter: true });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/plan-source/structures-overview`,
        { withCredentials: true });
      setAvailable((r.data?.lots || []).filter((l) => l.lot_id !== selectedLotId));
    } catch { setAvailable([]); }
  }, [projectId, selectedLotId]);
  useEffect(() => { load(); setSourceLotId(''); }, [load]);

  const run = async () => {
    if (!sourceLotId) return;
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/projects/${projectId}/plan-source?lot_id=${sourceLotId}`,
        { withCredentials: true },
      );
      const src = (r.data?.state || {}).structure || {};
      const added = duplicateFromFloor(src, opts);
      toast.success(
        `Dupliqué : ${added.walls} mur(s) porteur(s), ${added.stairs} escalier(s), ${added.openings} menuiserie(s) ext.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la duplication');
    } finally { setBusy(false); }
  };

  if (available.length === 0) return null;

  const OPT_LABELS = [
    ['porteurs', 'Murs porteurs'],
    ['withStairs', 'Escaliers'],
    ['extOpenings', 'Menuiseries ext.'],
    ['withOuter', 'Contour (si vide)'],
  ];

  return (
    <FoldableSection
      title="Dupliquer depuis un étage"
      icon={CopyPlus}
      iconClass="text-amber-300"
      defaultOpen={false}
      testid="duplicate-floor-section"
    >
      <p className="text-[9.5px] text-gray-500 mt-0.5 leading-snug">
        Copie la structure porteuse d'un autre niveau — superposition parfaite
        garantie. Tracez ensuite cloisons et menuiseries intérieures.
      </p>
      <select
        value={sourceLotId}
        onChange={(e) => setSourceLotId(e.target.value)}
        disabled={disabled}
        className="mt-1.5 w-full h-7 text-[11px] bg-black/30 border border-white/10 rounded px-1.5 text-gray-200 focus:outline-none [&>option]:bg-[#111]"
        data-testid="duplicate-source-select"
      >
        <option value="">Étage source…</option>
        {available.map((l) => (
          <option key={l.lot_id} value={l.lot_id}>{l.label} ({l.walls_count} murs)</option>
        ))}
      </select>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {OPT_LABELS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setOpts((o) => ({ ...o, [k]: !o[k] }))}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9.5px] ${
              opts[k] ? 'border-amber-400/50 text-amber-300 bg-amber-400/10' : 'border-white/10 text-gray-500'
            }`}
            data-testid={`duplicate-opt-${k}`}
          >
            {opts[k] && <Check className="w-2.5 h-2.5" />}
            {label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={run}
        disabled={disabled || busy || !sourceLotId}
        className="mt-2 w-full h-7 rounded bg-amber-400/90 hover:bg-amber-400 text-black text-[11px] font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
        data-testid="duplicate-floor-btn"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CopyPlus className="w-3 h-3" /> Dupliquer</>}
      </button>
    </FoldableSection>
  );
};
